import { COMPENSATION_KINDS, dailyCapacity, type AbsenceSpan, type PersonCalendarInput } from './capacity';
import { addDays, eachDay, isoWeekday, type ISODate } from './dates';
import type { AbsenceKind, AbsenceStatus } from './types';

/**
 * Leave and absences (Urlaub und Abwesenheiten).
 *
 * - Every absence a person enters is a request: it starts as "requested" and is decided by someone else, never by
 *   the person themselves. Leave and other kinds are approved by the person's approver (Genehmiger/in, set per
 *   person) or by holders of the right "Personal" (hr.manage). Sickness (also a sick child) is confirmed by
 *   "Personal" only, because a document may contain health data.
 * - Approved absences reduce the target hours (capacity) and appear in the team and Outlook calendars. A sick
 *   report counts provisionally from the moment it is entered (the person is not at work), until it is confirmed
 *   or rejected; other requests count only once approved.
 * - Colleagues see absences in the team calendar either by kind (kinds chosen in the settings, e.g. vacation)
 *   or as "abwesend". Health related kinds are never shown by kind to colleagues.
 * - Leave entitlement (days or hours per year) is entered by HR; the balance is computed from approved and
 *   requested vacation on working days (public holidays and non-working days do not count).
 */

export interface AbsenceKindRule {
  /** Needs a decision by someone else (approval, for sickness a confirmation). */
  approval: boolean;
  /** Counts while it waits for the decision (sickness: the person is absent anyway). */
  provisional: boolean;
  /** Counts against the yearly leave entitlement. */
  entitlement: boolean;
  /** The person can enter it; otherwise only HR (e.g. parental leave). */
  selfService: boolean;
  /** Health data: kind and documents visible only to the person and HR; decided by HR only. */
  health: boolean;
}

export const ABSENCE_RULES: Record<AbsenceKind, AbsenceKindRule> = {
  vacation: { approval: true, provisional: false, entitlement: true, selfService: true, health: false },
  special: { approval: true, provisional: false, entitlement: false, selfService: true, health: false },
  training: { approval: true, provisional: false, entitlement: false, selfService: true, health: false },
  overtime: { approval: true, provisional: false, entitlement: false, selfService: true, health: false },
  unpaid: { approval: true, provisional: false, entitlement: false, selfService: true, health: false },
  sick: { approval: true, provisional: true, entitlement: false, selfService: true, health: true },
  child_sick: { approval: true, provisional: true, entitlement: false, selfService: true, health: true },
  parental: { approval: true, provisional: false, entitlement: false, selfService: false, health: false },
  other: { approval: true, provisional: false, entitlement: false, selfService: true, health: false },
};

export function absenceRule(kind: string): AbsenceKindRule {
  return ABSENCE_RULES[kind as AbsenceKind] ?? ABSENCE_RULES.other;
}

/** Statuses that block the same days for another absence of the person. */
export const ACTIVE_ABSENCE_STATUSES: readonly AbsenceStatus[] = ['requested', 'approved'];

/** Kinds that count while waiting for the decision (sick reports). */
export const PROVISIONAL_KINDS: readonly AbsenceKind[] = (Object.keys(ABSENCE_RULES) as AbsenceKind[]).filter((k) => ABSENCE_RULES[k].provisional);

/** The absence reduces the target hours and shows in the calendars: approved, or a sick report not yet decided. */
export function absenceEffective(status: string, kind: string): boolean {
  return status === 'approved' || (status === 'requested' && absenceRule(kind).provisional);
}

/**
 * A document (e.g. sick note) is expected for this absence: from the configured calendar day of the absence on
 * (sickness: by default from the 4th calendar day, a sick child from the 1st). 0 = never expected.
 */
export function documentExpected(kind: string, from: ISODate, to: ISODate, fromDay: { sick: number; child_sick: number }): boolean {
  const day = kind === 'sick' ? fromDay.sick : kind === 'child_sick' ? fromDay.child_sick : 0;
  if (!day || day < 1) return false;
  const calendarDays = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
  return calendarDays >= day;
}

/** Label for colleagues: the kind if it is released for the team, otherwise "absent". */
export function teamAbsenceLabel(kind: string, teamVisibleKinds: readonly string[]): string {
  if (absenceRule(kind).health) return 'absent';
  return teamVisibleKinds.includes(kind) ? kind : 'absent';
}

/**
 * Minutes and working days an absence takes from the target hours, per date. One day of leave is the full
 * contractual day (base): half a day off is 0.5, also on a half public holiday (the morning), and a whole day
 * off on a half public holiday is 0.5 as well (the afternoon is the holiday).
 */
export function absenceUsage(calendar: PersonCalendarInput, a: AbsenceSpan, from: ISODate, to: ISODate): { date: ISODate; minutes: number; days: number }[] {
  const lo = a.from > from ? a.from : from;
  const hi = a.to < to ? a.to : to;
  if (lo > hi) return [];
  const saturdays = !!calendar.saturdayLeave && absenceRule(a.kind ?? 'other').entitlement && !a.halfDay && !a.minutes;
  // The Saturday rule looks at the whole absence (its first and last working day of leave); only then is the result cut
  // to the period. Cutting first lost a Saturday at the edge of a year, so the yearly balances did not add up to the
  // request (DAT-07, review of 29.10.2026).
  const days = dailyCapacity({ ...calendar, absences: [a] }, saturdays ? a.from : lo, saturdays ? a.to : hi);
  const out = days
    .filter((d) => d.absenceMinutes > 0)
    .map((d) => ({ date: d.date, minutes: d.absenceMinutes, days: d.baseMinutes > 0 ? d.absenceMinutes / d.baseMinutes : 0 }));
  // Turkey (setting): a Saturday inside whole days of leave is a leave day, unless it is a public holiday or the
  // contract has hours on it anyway (then it counted above); between the first and the last working day of leave only
  if (saturdays && out.length) {
    const first = out[0].date;
    const last = out[out.length - 1].date;
    for (const d of days) {
      if (isoWeekday(d.date) !== 6 || d.baseMinutes > 0 || d.holidayName || d.date < first || d.date > last) continue;
      out.push({ date: d.date, minutes: 0, days: 1 });
    }
    out.sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : 0));
  }
  return saturdays ? out.filter((u) => u.date >= lo && u.date <= hi) : out;
}

/**
 * Sickness during leave (request 24 of round 4): the days of a whole-day sickness are cut out of a whole-day leave.
 * Germany: days of incapacity certified by a doctor are not counted against the leave (§ 9 BUrlG); Turkey: sick leave
 * cannot be offset against annual leave (İş Kanunu art. 56 (4), Yıllık Ücretli İzin Yönetmeliği art. 6). The leave is not extended: the parts
 * before and after the sickness stay (0, 1 or 2 parts).
 */
export function cutLeave(leave: { from: ISODate; to: ISODate }, sick: { from: ISODate; to: ISODate }): { from: ISODate; to: ISODate }[] {
  if (sick.to < leave.from || sick.from > leave.to) return [{ from: leave.from, to: leave.to }];
  const out: { from: ISODate; to: ISODate }[] = [];
  if (sick.from > leave.from) out.push({ from: leave.from, to: addDays(sick.from, -1) });
  if (sick.to < leave.to) out.push({ from: addDays(sick.to, 1), to: leave.to });
  return out;
}

/**
 * Days chosen one by one in the calendar (request 24 of round 4) as ranges: days of one run belong together when only
 * days without working hours lie between them (weekend, public holiday, day off by contract) that no other absence
 * covers; such days count nothing, so the range takes exactly the chosen days. Chosen days without working hours count
 * nothing and are returned apart (`ignored`).
 */
export function daysToSpans(dates: ISODate[], workMinutes: (date: ISODate) => number, covered: (date: ISODate) => boolean): { spans: { from: ISODate; to: ISODate }[]; ignored: ISODate[] } {
  const sorted = [...new Set(dates)].sort();
  const ignored = sorted.filter((d) => workMinutes(d) <= 0);
  const picked = sorted.filter((d) => workMinutes(d) > 0);
  const spans: { from: ISODate; to: ISODate }[] = [];
  for (const d of picked) {
    const last = spans[spans.length - 1];
    if (last) {
      const gap = eachDay(addDays(last.to, 1), addDays(d, -1));
      if (gap.every((g) => workMinutes(g) <= 0 && !covered(g))) {
        last.to = d;
        continue;
      }
    }
    spans.push({ from: d, to: d });
  }
  return { spans, ignored };
}

/** Usage of an absence split by calendar year (leave entitlements are per year). */
export function usageByYear(calendar: PersonCalendarInput, a: AbsenceSpan): { year: number; days: number; minutes: number }[] {
  const out = new Map<number, { year: number; days: number; minutes: number }>();
  for (const u of absenceUsage(calendar, a, a.from, a.to)) {
    const year = Number(u.date.slice(0, 4));
    const y = out.get(year) ?? { year, days: 0, minutes: 0 };
    y.days += u.days;
    y.minutes += u.minutes;
    out.set(year, y);
  }
  return [...out.values()].sort((x, y) => x.year - y.year).map((y) => ({ ...y, days: Math.round(y.days * 100) / 100 }));
}

export interface LeaveEntitlement {
  unit: 'days' | 'hours';
  amount: number;
  carryover: number;
  carryoverUntil: ISODate | null;
}

export interface LeaveBalance {
  unit: 'days' | 'hours';
  /** null: no entitlement entered for the year */
  entitlement: number | null;
  carryover: number;
  carryoverUntil: ISODate | null;
  /** approved vacation up to today */
  taken: number;
  /** approved vacation after today */
  planned: number;
  /** vacation waiting for approval */
  requested: number;
  /** entitlement + carryover - taken - planned (null without entitlement) */
  available: number | null;
  /** available - requested */
  availableAfterRequests: number | null;
}

const round2 = (x: number) => Math.round(x * 100) / 100;

/**
 * Balance of a year. `calendar` holds contracts and holidays of the person (its absences are ignored);
 * `absences` are the person's vacation records (any status; only requested and approved count).
 */
export function leaveBalance(
  calendar: PersonCalendarInput,
  absences: (AbsenceSpan & { status: string; kind: string })[],
  entitlement: LeaveEntitlement | null,
  year: number,
  today: ISODate,
): LeaveBalance {
  const unit = entitlement?.unit ?? 'days';
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  let taken = 0;
  let planned = 0;
  let requested = 0;
  for (const a of absences) {
    if (!absenceRule(a.kind).entitlement) continue;
    if (a.status !== 'approved' && a.status !== 'requested') continue;
    for (const u of absenceUsage({ ...calendar, absences: [] }, a, from, to)) {
      const v = unit === 'days' ? u.days : u.minutes / 60;
      if (a.status === 'requested') requested += v;
      else if (u.date <= today) taken += v;
      else planned += v;
    }
  }
  const total = entitlement ? entitlement.amount + entitlement.carryover : null;
  const available = total === null ? null : round2(total - taken - planned);
  return {
    unit,
    entitlement: entitlement ? entitlement.amount : null,
    carryover: entitlement?.carryover ?? 0,
    carryoverUntil: entitlement?.carryoverUntil ?? null,
    taken: round2(taken),
    planned: round2(planned),
    requested: round2(requested),
    available,
    availableAfterRequests: available === null ? null : round2(available - requested),
  };
}

export interface OvertimeBalance {
  /** first day of the calculation: Erfassungsbeginn (or 1 January), not before the entry date */
  since: ISODate;
  /** booked minus target from `since` to yesterday, plus extra hours booked today */
  earnedMinutes: number;
  /** paid out (negative) and corrected by HR (either way), see OvertimeAdjustment */
  adjustedMinutes: number;
  /** approved time off in lieu from today on */
  plannedMinutes: number;
  /** requested time off in lieu from today on */
  requestedMinutes: number;
  /** earned + adjusted - planned */
  availableMinutes: number;
  /** available - requested */
  availableAfterRequestsMinutes: number;
}

/** Extra hours paid out with the salary (negative) or corrected by HR (either way). */
export interface OvertimeAdjustment {
  kind: 'payout' | 'correction';
  minutes: number;
  /** payout: first day of the payroll month; correction: the day it applies from */
  date: ISODate;
}

/**
 * Extra hours (Mehrarbeit) and time off in lieu (Freizeitausgleich), derived from the booked hours:
 * booked minus target per day from `since` to yesterday. Today counts only with more than the target booked
 * (a Saturday booked in the morning counts at once, a working day not yet booked does not).
 * Extra hours are either taken as time off in lieu or paid out: past time off in lieu needs no extra step (the
 * day stays in the target and has no bookings); approved time off in lieu from today on is "planned", requested
 * time off in lieu from today on is "requested"; a payout entered by HR lowers the balance at once.
 * `calendar` holds the approved absences; `requested` the open requests for time off in lieu.
 * This is a guide for the decision, not a working time account (see docs/KPI.md).
 */
export function overtimeBalance(
  calendar: PersonCalendarInput,
  requested: AbsenceSpan[],
  bookedByDate: ReadonlyMap<ISODate, number>,
  since: ISODate,
  today: ISODate,
  adjustments: readonly OvertimeAdjustment[] = [],
): OvertimeBalance {
  const start = calendar.entryDate && calendar.entryDate > since ? calendar.entryDate : since;
  const yesterday = addDays(today, -1);
  let earned = 0;
  if (start <= yesterday) {
    for (const d of dailyCapacity(calendar, start, yesterday)) earned += (bookedByDate.get(d.date) ?? 0) - d.targetMinutes;
  }
  if (start <= today) {
    const [t] = dailyCapacity(calendar, today, today);
    earned += Math.max(0, (bookedByDate.get(today) ?? 0) - t.targetMinutes);
  }
  const future = (spans: AbsenceSpan[]) =>
    spans
      .filter((a) => COMPENSATION_KINDS.includes(a.kind ?? '') && a.to >= today)
      .reduce((s, a) => s + absenceUsage({ ...calendar, absences: [] }, a, a.from > today ? a.from : today, a.to).reduce((x, u) => x + u.minutes, 0), 0);
  const planned = future(calendar.absences);
  const requestedMinutes = future(requested);
  const adjusted = adjustments.reduce((s, a) => s + a.minutes, 0);
  return {
    since: start,
    earnedMinutes: earned,
    adjustedMinutes: adjusted,
    plannedMinutes: planned,
    requestedMinutes,
    availableMinutes: earned + adjusted - planned,
    availableAfterRequestsMinutes: earned + adjusted - planned - requestedMinutes,
  };
}

export interface OvertimeMonth {
  /** first and last day counted (the month, not before `since` or the entry date, not after today) */
  from: ISODate | null;
  to: ISODate | null;
  targetMinutes: number;
  bookedMinutes: number;
  /** booked - target of the month (today only with more than the target booked, like the balance) */
  differenceMinutes: number;
  /** hours on days without work obligation: weekends and other days off of the contract */
  daysOffMinutes: number;
  /** hours on public holidays (beyond what is left of a half holiday) */
  holidayMinutes: number;
  /** hours on days of approved absence (leave, sickness, time off in lieu) beyond what is left of the day */
  absenceDayMinutes: number;
  /** hours above the target on ordinary working days */
  workdayExtraMinutes: number;
  /** time off in lieu taken in the month (days up to today) */
  compensationMinutes: number;
}

/**
 * Monthly breakdown for payroll: where the extra hours of a month were worked. Every positive hour lands in
 * exactly one of daysOff, holiday, absenceDay or workdayExtra; missing hours on working days lower the
 * difference only. A guide from the project bookings, not a working time record.
 */
export function overtimeMonth(
  calendar: PersonCalendarInput,
  bookedByDate: ReadonlyMap<ISODate, number>,
  month: ISODate,
  today: ISODate,
  since: ISODate,
): OvertimeMonth {
  const first = `${month.slice(0, 7)}-01`;
  const last = addDays(addMonthFirst(first), -1);
  let from = first < since ? since : first;
  if (calendar.entryDate && calendar.entryDate > from) from = calendar.entryDate;
  const to = last > today ? today : last;
  const out: OvertimeMonth = {
    from: null,
    to: null,
    targetMinutes: 0,
    bookedMinutes: 0,
    differenceMinutes: 0,
    daysOffMinutes: 0,
    holidayMinutes: 0,
    absenceDayMinutes: 0,
    workdayExtraMinutes: 0,
    compensationMinutes: 0,
  };
  if (from > to) return out;
  out.from = from;
  out.to = to;
  for (const d of dailyCapacity(calendar, from, to)) {
    const booked = bookedByDate.get(d.date) ?? 0;
    const diff = booked - d.targetMinutes;
    out.targetMinutes += d.targetMinutes;
    out.bookedMinutes += booked;
    out.compensationMinutes += d.compensationMinutes;
    out.differenceMinutes += d.date === today ? Math.max(0, diff) : diff;
    const extra = Math.max(0, diff);
    if (d.baseMinutes === 0) out.daysOffMinutes += extra;
    else if (d.holidayMinutes > 0) out.holidayMinutes += extra;
    else if (d.absenceMinutes > 0) out.absenceDayMinutes += extra;
    else out.workdayExtraMinutes += extra;
  }
  return out;
}

/** First day of the following month. */
function addMonthFirst(first: ISODate): ISODate {
  const y = Number(first.slice(0, 4));
  const m = Number(first.slice(5, 7));
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
}
